import { Button, Heading, toast } from '@medusajs/ui';

import { getOrderCancelErrorMessage } from '@lib/util/get-order-cancel-error-message';
import { useCancelOrder } from '@lib/query/mutations/orders';
import Modal from '@modules/common/components/modal';

interface OrderCancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  orderNumber: string;
}

// cache invalidation 由 useCancelOrder 內部處理，這裡只負責 UI 回饋。
export const OrderCancelModal = ({
  isOpen,
  onClose,
  orderId,
  orderNumber,
}: OrderCancelModalProps) => {
  const { mutate: cancelOrderMutation, isPending } = useCancelOrder({
    onSuccess: () => {
      toast.success('訂單已取消');
      onClose();
    },
    onError: (error) => {
      toast.error(getOrderCancelErrorMessage(error));
    },
  });

  const handleConfirm = () => {
    cancelOrderMutation({ orderId });
  };

  return (
    <Modal
      isOpen={isOpen}
      close={onClose}
      // 請求飛行中禁止背景點擊/ESC 關閉，避免 toast 回饋消失
      autoClose={!isPending}
      data-testid="order-cancel-modal"
      panelClassName="max-h-[90vh] max-w-[500px] border-0 bg-white-10"
    >
      <Modal.Title>
        <Heading className="mb-4 text-xl font-bold">確認取消訂單</Heading>
      </Modal.Title>
      <Modal.Body>
        <div className="flex w-full flex-col gap-y-4">
          <p className="text-black-50 text-base">
            確定要取消訂單 <strong>#{orderNumber}</strong> 嗎？
          </p>
          <p className="text-black-30 text-sm">
            取消後，已付款金額將自動退還至原付款帳戶。此操作無法復原。
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer className="mt-6">
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          disabled={isPending}
          className="border-black-10 text-black-10 flex-none rounded-sm border border-solid px-4 py-2"
          data-testid="order-cancel-modal-close-button"
        >
          返回
        </Button>
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={isPending}
          className="text-white-10 flex-none rounded-sm bg-rose-500 px-4 py-2 shadow-none hover:bg-rose-600"
          data-testid="order-cancel-modal-submit-button"
        >
          {isPending ? '取消中...' : '確認取消'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderCancelModal;
